import React, { useState, useRef, useCallback, useContext } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudUploadAlt,
  faTimes,
  faImage,
  faVideo,
  faFile,
  faTrash,
  faDownload,
  faCopy,
  faCheck,
  faExclamationTriangle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { uploadFile, uploadMultipleFiles, validateFile, getFileIcon, FILE_TYPES } from '../utils/storage';
import Toast from './Toast';
import { SettingsContext } from '../contexts/SettingsContext';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--radius-2xl);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  border: 1px solid var(--border-color-light);
  position: relative;
`;

const ModalHeader = styled.div`
  padding: var(--space-6);
  border-bottom: 1px solid var(--border-color-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--surface-color);
`;

const HeaderTitle = styled.h2`
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
  font-family: var(--font-heading-bold);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-lg);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--neutral-100);
    color: var(--text-color);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const ModalContent = styled.div`
  padding: var(--space-6);
  max-height: calc(80vh - 120px);
  overflow-y: auto;
`;

const UploadZone = styled.div`
  border: 2px dashed var(--border-color-light);
  border-radius: var(--radius-xl);
  padding: var(--space-8);
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--background-color);
  position: relative;

  ${props => props.isDragOver && css`
    border-color: var(--primary-600);
    background: var(--primary-50);
    transform: scale(1.02);
  `}

  &:hover {
    border-color: var(--primary-400);
    background: var(--primary-50);
  }

  &:focus-within {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const UploadIcon = styled.div`
  font-size: var(--text-4xl);
  color: var(--text-secondary);
  margin-bottom: var(--space-4);
  transition: color 0.3s ease;

  ${UploadZone}:hover & {
    color: var(--primary-600);
  }
`;

const UploadText = styled.div`
  font-size: var(--text-lg);
  color: var(--text-color);
  margin-bottom: var(--space-2);
  font-weight: 500;
`;

const UploadSubtext = styled.div`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin-bottom: var(--space-4);
`;

const FileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  cursor: pointer;
`;

const FileTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: var(--space-3);
  margin-top: var(--space-4);
`;

const FileTypeItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  background: var(--neutral-50);
  border: 1px solid var(--border-color-light);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-300);
    transform: translateY(-1px);
  }
`;

const FileTypeIcon = styled.div`
  font-size: var(--text-xl);
  color: var(--text-secondary);
  margin-bottom: var(--space-2);
`;

const FileTypeText = styled.div`
  font-size: var(--text-xs);
  color: var(--text-secondary);
  text-align: center;
`;

const FileList = styled.div`
  margin-top: var(--space-6);
  space-y: var(--space-3);
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4);
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-lg);
  background: var(--background-color);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: var(--primary-300);
    background: var(--primary-50);
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex: 1;
`;

const FileIcon = styled.div`
  font-size: var(--text-lg);
  color: var(--text-secondary);
`;

const FileDetails = styled.div`
  flex: 1;
`;

const FileName = styled.div`
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-color);
  margin-bottom: var(--space-1);
`;

const FileSize = styled.div`
  font-size: var(--text-xs);
  color: var(--text-secondary);
`;

const FileActions = styled.div`
  display: flex;
  gap: var(--space-2);
  align-items: center;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-md);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--neutral-100);
    color: var(--text-color);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: var(--neutral-200);
  border-radius: 2px;
  overflow: hidden;
  margin-top: var(--space-2);
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, var(--primary-600), var(--primary-500));
  border-radius: 2px;
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

const StatusIcon = styled.div`
  font-size: var(--text-sm);
  color: ${props => {
    switch (props.status) {
      case 'success': return 'var(--success-600)';
      case 'error': return 'var(--error-600)';
      case 'uploading': return 'var(--primary-600)';
      default: return 'var(--text-secondary)';
    }
  }};
`;

const ModalFooter = styled.div`
  padding: var(--space-6);
  border-top: 1px solid var(--border-color-light);
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  background: var(--surface-color);
`;

const Button = styled.button`
  padding: var(--space-3) var(--space-5);
  border-radius: var(--radius-lg);
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
  font-family: var(--font-family);

  ${props => props.primary && css`
    background: linear-gradient(135deg, var(--primary-600), var(--primary-700));
    color: white;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--primary-700), var(--primary-800));
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
    }
  `}

  ${props => props.secondary && css`
    background: var(--surface-color);
    color: var(--text-color);
    border-color: var(--border-color-light);

    &:hover:not(:disabled) {
      background: var(--neutral-50);
      border-color: var(--primary-300);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid var(--neutral-300);
  border-radius: 50%;
  border-top-color: var(--primary-600);
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

function FileUploadModal({ isOpen, onClose, userId, conversationId = null, onUploadComplete }) {
  const { settings } = useContext(SettingsContext);
  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, []);

  const handleFileInput = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
    e.target.value = ''; // Reset input
  }, []);

  const handleFiles = useCallback((newFiles) => {
    const validFiles = newFiles.filter(file => {
      const validation = validateFile(file);
      if (!validation.valid) {
        showToast(validation.error, 'error');
        return false;
      }
      return true;
    });

    setFiles(prev => [...prev, ...validFiles.map(file => ({
      file,
      id: Date.now() + Math.random(),
      status: 'pending',
      progress: 0,
      error: null
    }))]);
  }, [showToast]);

  const removeFile = useCallback((fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  const uploadFiles = useCallback(async () => {
    if (files.length === 0 || uploading) return;

    setUploading(true);

    try {
      const filesToUpload = files.filter(f => f.status === 'pending');

      // Update status to uploading
      setFiles(prev => prev.map(f =>
        filesToUpload.find(ftu => ftu.id === f.id)
          ? { ...f, status: 'uploading' }
          : f
      ));

      const results = await uploadMultipleFiles(userId, filesToUpload.map(f => f.file), {
        conversationId,
        groupIdGenerator: (_file, idx) => {
          const base = `${Date.now()}_${idx}_${_file.name}`;
          try { return btoa(unescape(encodeURIComponent(base))).replace(/=+$/,''); } catch { return `${Date.now()}_${idx}`; }
        },
        onProgress: (index, progress) => {
          setFiles(prev => prev.map(f =>
            f.id === filesToUpload[index].id
              ? { ...f, progress }
              : f
          ));
        }
      });

      // Update file statuses
      setFiles(prev => prev.map(f => {
        const result = results.results.find(r => r.metadata.name === f.file.name);
        const error = results.errors.find(e => e.file === f.file.name);

        if (result) {
          return { ...f, status: 'success', downloadURL: result.downloadURL };
        } else if (error) {
          return { ...f, status: 'error', error: error.error };
        }
        return f;
      }));

      if (results.results.length > 0) {
        showToast(`Successfully uploaded ${results.results.length} file(s)`, 'success');
        // Optional: convert each uploaded file to Markdown when enhanced processing is ON
        if (settings?.enhancedDocProcessing !== false) {
          for (const r of results.results) {
            try {
              if (!r.downloadURL) continue;
              const resp = await fetch('/api/convert', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: r.downloadURL, filename: r.metadata?.name, mime: r.metadata?.type || r.type })
              });
              if (resp.ok) {
                const data = await resp.json();
                if (data.success && data.markdown) {
                  const mdFile = new File([data.markdown], `converted.md`, { type: 'text/markdown' });
                  await uploadFile(userId, mdFile, { conversationId, fileGroupId: r.metadata?.groupId || null, overrideName: 'converted.md' });
                }
              }
            } catch (e) {
              console.warn('Markdown conversion upload skipped:', e);
            }
          }
        }
        if (onUploadComplete) onUploadComplete(results.results);
      }

      if (results.errors.length > 0) {
        showToast(`Failed to upload ${results.errors.length} file(s)`, 'error');
      }

    } catch (error) {
      showToast(`Upload failed: ${error.message}`, 'error');
    } finally {
      setUploading(false);
    }
  }, [files, uploading, userId, conversationId, onUploadComplete, showToast]);

  const clearCompleted = useCallback(() => {
    setFiles(prev => prev.filter(f => f.status === 'pending' || f.status === 'uploading'));
  }, []);

  const clearAll = useCallback(() => {
    setFiles([]);
  }, []);

  if (!isOpen) return null;

  const pendingFiles = files.filter(f => f.status === 'pending');
  const completedFiles = files.filter(f => f.status === 'success');
  const failedFiles = files.filter(f => f.status === 'error');
  const uploadingFiles = files.filter(f => f.status === 'uploading');

  return (
    <>
      <ModalOverlay onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <HeaderTitle>Upload Files</HeaderTitle>
            <CloseButton onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          </ModalHeader>

          <ModalContent>
            <UploadZone
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon>
                <FontAwesomeIcon icon={faCloudUploadAlt} />
              </UploadIcon>
              <UploadText>Drop files here or click to browse</UploadText>
              <UploadSubtext>
                Supports images, videos, documents, and more (max 50MB per file)
              </UploadSubtext>

              <FileInput
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.doc,.docx,.txt,.md,.csv,.json,.zip"
                onChange={handleFileInput}
              />
            </UploadZone>

            <FileTypeGrid>
              <FileTypeItem>
                <FileTypeIcon>
                  <FontAwesomeIcon icon={faImage} />
                </FileTypeIcon>
                <FileTypeText>Images</FileTypeText>
              </FileTypeItem>
              <FileTypeItem>
                <FileTypeIcon>
                  <FontAwesomeIcon icon={faVideo} />
                </FileTypeIcon>
                <FileTypeText>Videos</FileTypeText>
              </FileTypeItem>
              <FileTypeItem>
                <FileTypeIcon>
                  <FontAwesomeIcon icon={faFile} />
                </FileTypeIcon>
                <FileTypeText>Documents</FileTypeText>
              </FileTypeItem>
              <FileTypeItem>
                <FileTypeIcon>
                  <FontAwesomeIcon icon={faFile} />
                </FileTypeIcon>
                <FileTypeText>Archives</FileTypeText>
              </FileTypeItem>
            </FileTypeGrid>

            {files.length > 0 && (
              <FileList>
                {files.map((fileData) => (
                  <FileItem key={fileData.id}>
                    <FileInfo>
                      <FileIcon>
                        <FontAwesomeIcon icon={getFileIcon(fileData.file.type)} />
                      </FileIcon>
                      <FileDetails>
                        <FileName>{fileData.file.name}</FileName>
                        <FileSize>
                          {(fileData.file.size / 1024 / 1024).toFixed(2)} MB
                        </FileSize>
                        {fileData.status === 'uploading' && (
                          <ProgressBar>
                            <ProgressFill progress={fileData.progress} />
                          </ProgressBar>
                        )}
                      </FileDetails>
                    </FileInfo>

                    <FileActions>
                      <StatusIcon status={fileData.status}>
                        {fileData.status === 'uploading' && <FontAwesomeIcon icon={faSpinner} spin />}
                        {fileData.status === 'success' && <FontAwesomeIcon icon={faCheck} />}
                        {fileData.status === 'error' && <FontAwesomeIcon icon={faExclamationTriangle} />}
                        {fileData.status === 'pending' && <FontAwesomeIcon icon={faCloudUploadAlt} />}
                      </StatusIcon>

                      {fileData.status === 'success' && fileData.downloadURL && (
                        <ActionButton
                          onClick={() => navigator.clipboard.writeText(fileData.downloadURL)}
                          title="Copy URL"
                        >
                          <FontAwesomeIcon icon={faCopy} />
                        </ActionButton>
                      )}

                      <ActionButton
                        onClick={() => removeFile(fileData.id)}
                        title="Remove"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </ActionButton>
                    </FileActions>
                  </FileItem>
                ))}
              </FileList>
            )}
          </ModalContent>

          <ModalFooter>
            <Button secondary onClick={clearAll} disabled={files.length === 0}>
              Clear All
            </Button>
            <Button secondary onClick={clearCompleted} disabled={completedFiles.length === 0 && failedFiles.length === 0}>
              Clear Completed
            </Button>
            <Button
              primary
              onClick={uploadFiles}
              disabled={pendingFiles.length === 0 || uploading}
            >
              {uploading ? (
                <>
                  <LoadingSpinner />
                  Uploading...
                </>
              ) : (
                `Upload ${pendingFiles.length} File${pendingFiles.length !== 1 ? 's' : ''}`
              )}
            </Button>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}

export default FileUploadModal;
